import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { useRole } from '../context/RoleContext';

/**
 * AppReadyGate
 *
 * Blocks the Firestore-heavy provider tree from rendering until:
 *   • auth resolved
 *   • companyId resolved
 *   • membership resolved (role !== undefined) via RoleProvider (M6 dedup)
 *
 * Sprint 286 (M4 + M6): the readiness signal is now derived from the
 * hydrated context state. It NO LONGER performs its own
 * companies/{cid}/users/{uid} `getDoc` — RoleProvider is the single source
 * of truth for the membership read.
 *
 * The useEffect's dependency array is intentionally free of
 * `location.pathname`. Readiness is a function of (user, company,
 * membership) — not of which authenticated page you're viewing — so
 * ordinary tab-to-tab navigation no longer causes the full-screen
 * spinner to reappear.
 */
const AppReadyGate = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, isAuthLoading } = useAuth();
  const { companyId, isCompanyLoading } = useCompany();
  const { role, isRoleHydrating } = useRole();

  const hydrating = isAuthLoading || isCompanyLoading || isRoleHydrating;

  // Missing-membership + onboarding redirects — driven purely by hydrated
  // context, not by route changes.
  useEffect(() => {
    if (hydrating) return;
    if (!user) return; // Parent (ProtectedRoute) handles the /login redirect.

    // Identity present but no company context → cannot enter the app shell.
    if (!companyId) {
      navigate('/login', { replace: true });
      return;
    }

    // Membership doc missing / no role assigned.
    if (role === null) {
      navigate('/login', { replace: true });
      return;
    }

    // Onboarding gate — allow the onboarding route through to prevent
    // a redirect loop.
    if (profile?.onboardingComplete === false && !location.pathname.startsWith('/onboarding')) {
      navigate('/onboarding', { replace: true });
    }
  }, [hydrating, user, companyId, role, profile?.onboardingComplete]); // eslint-disable-line react-hooks/exhaustive-deps

  if (hydrating) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#71DED6]" />
      </div>
    );
  }

  // Silent-render while the useEffect above navigates to a recovery route.
  if (!companyId || role === null) return null;
  if (profile?.onboardingComplete === false && !location.pathname.startsWith('/onboarding')) {
    return null;
  }

  return <>{children}</>;
};

export default AppReadyGate;
